import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Rocket } from 'lucide-react';
import { useStartDeployment, useVersions } from '@/services/queries';
import { ApiError } from '@/services/api/client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';

const schema = Yup.object({
  versionId: Yup.string().uuid().required('Choose a version'),
  environment: Yup.string().oneOf(['staging', 'production']).required(),
  notes: Yup.string().max(2000),
});

/**
 * Starting a deployment.
 *
 * The version is chosen from what has actually been published rather than typed
 * as an id: a deployment pointing at a version that does not exist fails deep in
 * the pipeline, long after the person who started it stopped watching.
 *
 * Defaults to staging. Production is a deliberate second choice, not the path of
 * least resistance.
 */
export function StartDeploymentDialog() {
  const [open, setOpen] = useState(false);
  const start = useStartDeployment();

  // Only fetched once the dialog opens: this list is not needed to render the
  // page behind it, and it is a second request on every deployments view.
  const versions = useVersions({ limit: 50, offset: 0 });

  const form = useFormik({
    initialValues: {
      versionId: '',
      environment: 'staging' as 'staging' | 'production',
      notes: '',
    },
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      try {
        await start.mutateAsync({
          versionId: values.versionId,
          environment: values.environment,
          ...(values.notes.trim() ? { notes: values.notes.trim() } : {}),
        });
        helpers.resetForm();
        setOpen(false);
      } catch (error) {
        if (error instanceof ApiError && error.fieldErrors) {
          const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), form.values);
          helpers.setErrors(placed);
        }
      }
    },
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) form.resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button className="ml-auto">
          <Rocket className="size-4" />
          Start deployment
        </Button>
      </DialogTrigger>

      <DialogContent>
        <form onSubmit={form.handleSubmit}>
          <DialogHeader>
            <DialogTitle>Start a deployment</DialogTitle>
            <DialogDescription>
              Moves a published build into an environment. It begins at <em>queued</em>; stages are
              advanced explicitly from the list.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="deployment-version">Version</Label>
              <Select
                value={form.values.versionId}
                onValueChange={(value) => {
                  void form.setFieldValue('versionId', value);
                }}
              >
                <SelectTrigger id="deployment-version">
                  <SelectValue
                    placeholder={versions.isPending ? 'Loading versions…' : 'Choose a version'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {versions.data?.data.map((version) => (
                    <SelectItem key={version.id} value={version.id}>
                      {version.versionNumber} · {version.releaseChannel}
                      {version.codeSigned ? '' : ' · unsigned'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.touched.versionId && form.errors.versionId && (
                <p className="text-sm text-destructive">{form.errors.versionId}</p>
              )}
              {versions.data?.data.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Nothing published yet — publish a version first.
                </p>
              )}
            </div>

            <div className="grid gap-2">
              <Label htmlFor="deployment-environment">Environment</Label>
              <Select
                value={form.values.environment}
                onValueChange={(value) => {
                  void form.setFieldValue('environment', value);
                }}
              >
                <SelectTrigger id="deployment-environment">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staging">Staging</SelectItem>
                  <SelectItem value="production">Production</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="deployment-notes">Notes</Label>
              <textarea
                id="deployment-notes"
                name="notes"
                rows={3}
                value={form.values.notes}
                onChange={form.handleChange}
                maxLength={2000}
                placeholder="What is in this release, or why it is going out."
                className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
              />
            </div>

            {form.values.environment === 'production' && (
              <p className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm">
                This puts the build in front of customers.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={form.isSubmitting || start.isPending}>
              Start
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
